import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../hooks/ThemeContext";
import {
  Globe,
  ShoppingBag,
  Layers,
  RefreshCw,
  Shield,
  TrendingUp,
  MessageSquare,
  FileText,
  Palette,
  Code2,
  Rocket,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const SERVICES = [
  {
    Icon: Globe,
    accent: "rose",
    title: "Site vitrine",
    desc: "Présentez votre activité avec un site moderne, responsive et optimisé SEO.",
    features: ["Design sur-mesure", "Responsive mobile", "SEO optimisé"],
  },
  {
    Icon: ShoppingBag,
    accent: "fuchsia",
    title: "Site e-commerce",
    desc: "Boutique en ligne performante avec gestion catalogue et paiement sécurisé.",
    features: ["Gestion catalogue", "Paiement sécurisé", "UX optimisée"],
  },
  {
    Icon: Layers,
    accent: "violet",
    title: "Application web",
    desc: "Plateformes métiers, portails clients et outils web personnalisés.",
    features: ["Architecture solide", "Interface intuitive", "Scalable"],
  },
  {
    Icon: RefreshCw,
    accent: "blue",
    title: "Refonte de site",
    desc: "Modernisez votre existant pour booster conversions, image et SEO.",
    features: ["Audit préalable", "Migration sécurisée", "Perf. améliorées"],
  },
  {
    Icon: Shield,
    accent: "emerald",
    title: "Maintenance & support",
    desc: "Mises à jour, sécurité et évolutions pour pérenniser votre projet.",
    features: ["Monitoring 24/7", "MAJ régulières", "Support réactif"],
  },
  {
    Icon: TrendingUp,
    accent: "orange",
    title: "SEO & visibilité",
    desc: "Audit, optimisation et suivi pour gagner en visibilité sur Google.",
    features: ["Audit complet", "Optimisation technique", "Suivi positions"],
  },
];

const PROCESS = [
  {
    Icon: MessageSquare,
    num: "1",
    title: "Échange",
    desc: "Nous discutons de vos besoins, objectifs et contraintes.",
  },
  {
    Icon: FileText,
    num: "2",
    title: "Proposition",
    desc: "Devis personnalisé et planning de projet détaillé.",
  },
  {
    Icon: Palette,
    num: "3",
    title: "Design",
    desc: "Maquettes validées ensemble avant tout développement.",
  },
  {
    Icon: Code2,
    num: "4",
    title: "Développement",
    desc: "Code propre, testé et livré dans les délais convenus.",
  },
  {
    Icon: Rocket,
    num: "5",
    title: "Lancement",
    desc: "Mise en ligne soignée et accompagnement post-livraison.",
  },
];

const STATS = [
  { value: "50+", label: "Projets livrés" },
  { value: "98%", label: "Clients satisfaits" },
  { value: "< 24h", label: "Délai de réponse" },
];

const ACCENT = {
  rose: { dot: "bg-rose-400", icon: "text-rose-400" },
  fuchsia: { dot: "bg-fuchsia-400", icon: "text-fuchsia-400" },
  violet: { dot: "bg-violet-400", icon: "text-violet-400" },
  blue: { dot: "bg-blue-400", icon: "text-blue-400" },
  emerald: { dot: "bg-emerald-400", icon: "text-emerald-400" },
  orange: { dot: "bg-orange-400", icon: "text-orange-400" },
};

const Services = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const bgStyle = { backgroundColor: isDark ? "#07070a" : "#ffffff" };
  const heroBg = isDark
    ? "border-b border-white/[0.06]"
    : "border-b border-gray-100 bg-gray-50";
  const card = isDark
    ? "border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.06] hover:border-white/[0.14]"
    : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-xl";
  const iconBg = isDark ? "bg-white/[0.06]" : "bg-zinc-100";
  const sub = isDark ? "text-gray-500" : "text-gray-500";
  const cTxt = isDark ? "text-gray-400" : "text-zinc-700";
  const pill = isDark
    ? "border-white/[0.08] bg-white/[0.04] text-gray-300"
    : "border-gray-200 bg-white text-gray-700";
  const sectBg = isDark
    ? "border-y border-white/[0.06]"
    : "bg-gray-50 border-y border-gray-100";
  const rightCard = isDark
    ? "border-white/[0.08] bg-white/[0.04]"
    : "border-gray-200 bg-white";
  const sep = isDark
    ? "bg-gradient-to-r from-transparent via-white/[0.06] to-transparent"
    : "bg-gradient-to-r from-transparent via-gray-200 to-transparent";

  return (
    <div
      className={`flex-1 flex flex-col ${isDark ? "text-white" : "text-gray-900"}`}
      style={bgStyle}
    >
      {/* ── HERO ──────────────────────────────────────────── */}
      <div className={`relative overflow-hidden ${heroBg}`}>
        {/* halos identité violet/rose */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className={`absolute -left-48 -top-48 h-[40rem] w-[40rem] rounded-full blur-[160px] ${isDark ? "bg-rose-500/[0.18]" : "bg-rose-300/20"}`}
          />
          <div
            className={`absolute -right-36 top-0 h-[32rem] w-[32rem] rounded-full blur-[140px] ${isDark ? "bg-violet-600/[0.20]" : "bg-violet-200/20"}`}
          />
          <div
            className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-40 w-[60%] rounded-full blur-[100px] ${isDark ? "bg-purple-700/[0.12]" : ""}`}
          />
        </div>
        <div className="relative container mx-auto px-4 py-16 lg:py-24 max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-center">
            {/* Left */}
            <div className="space-y-7">
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${pill}`}
              >
                Agence de développement web premium
              </div>

              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight">
                Des services pensés
                <br />
                pour votre{" "}
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
                  }}
                >
                  succès
                </span>
              </h1>

              <p className={`text-lg max-w-lg leading-relaxed ${sub}`}>
                De la création à la maintenance, nous couvrons l'intégralité de
                vos besoins digitaux avec une approche 100&nbsp;% sur-mesure.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-white transition-all hover:-translate-y-0.5"
                  style={{
                    background:
                      "linear-gradient(135deg, #f43f5e, #a855f7, #6366f1)",
                  }}
                >
                  Demander un devis gratuit
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="#services"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold border transition-all duration-300 hover:-translate-y-0.5 ${
                    isDark
                      ? "border-white/10 bg-white/5 hover:bg-white/10 text-white"
                      : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  Voir les services
                </a>
              </div>
            </div>

            {/* Right — stats + checklist */}
            <div
              className={`rounded-3xl border p-8 shadow-xl space-y-6 ${rightCard}`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-widest ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                En chiffres
              </p>
              <div className="space-y-5">
                {STATS.map(({ value, label }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between"
                  >
                    <span className={cTxt}>{label}</span>
                    <span className="text-2xl font-extrabold bg-gradient-to-r from-rose-400 to-violet-500 bg-clip-text text-transparent">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
              <hr className={isDark ? "border-white/10" : "border-gray-100"} />
              <div className="space-y-3">
                {[
                  "Devis gratuit & sans engagement",
                  "Équipe dédiée à votre projet",
                  "Livraison dans les délais",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-rose-400 flex-shrink-0" />
                    <span className={`text-sm ${cTxt}`}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`h-[2px] w-full ${sep}`} />

      {/* ── SERVICES GRID ─────────────────────────────────── */}
      <div id="services" className="container mx-auto px-4 py-20 max-w-7xl">
        <div className="text-center mb-14 space-y-3">
          <p className="text-sm font-semibold uppercase tracking-widest text-rose-400">
            Ce que je propose
          </p>
          <h2 className="text-3xl lg:text-4xl font-extrabold">
            Toutes mon expertises,
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
              }}
            >
              {" "}
              à votre service
            </span>
          </h2>
          <p className={`max-w-xl mx-auto ${sub}`}>
            Chaque prestation est pensée pour livrer un résultat concret et
            mesurable.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-2">
          {SERVICES.map(({ Icon, accent, title, desc, features }) => {
            const a = ACCENT[accent];
            return (
              <div
                key={title}
                className={`group relative rounded-3xl p-2 border shadow-xl flex flex-col gap-5 transition-colors duration-300 ${card}`}
              >
                <div
                  className={`inline-flex p-4 rounded-2xl transition-transform  ${iconBg} shadow-xl`}
                >
                  <Icon
                    className={`duration-300 group-hover:-translate-y-1 group-hover:scale-110 w-7 h-7 ${a.icon}`}
                  />
                </div>

                <div className="flex-1 space-y-2">
                  <h3 className="text-lg font-bold">{title}</h3>
                  <p className={`text-sm leading-relaxed ${cTxt}`}>{desc}</p>
                </div>

                <ul className="space-y-2">
                  {features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5">
                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.dot}`}
                      />
                      <span className={`text-sm ${cTxt}`}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`h-[2px] w-full ${sep}`} />

      {/* ── PROCESS ───────────────────────────────────────── */}
      <div className={sectBg}>
        <div className="container mx-auto px-4 py-20 max-w-7xl">
          <div className="text-center mb-14 space-y-3">
            <p className="text-sm font-semibold uppercase tracking-widest text-rose-400">
              Notre méthode
            </p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">
              5 étapes vers votre succès
            </h2>
            <p className={`max-w-xl mx-auto ${sub}`}>
              Un process éprouvé qui garantit transparence, qualité et respect
              des délais.
            </p>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-5 gap-8">
            {/* Ligne de connexion desktop */}
            <div
              className={`hidden md:block absolute top-10 left-[10%] right-[10%] h-px ${
                isDark ? "bg-white/10" : "bg-gray-200"
              }`}
            />

            {PROCESS.map(({ Icon, num, title, desc }) => (
              <div
                key={num}
                className="relative flex flex-col items-center text-center gap-4"
              >
                <div
                  className={`relative z-10 inline-flex h-20 w-20 items-center justify-center rounded-full border-2 shadow-xl ${iconBg} ${
                    isDark ? "border-white/10" : "border-gray-200"
                  }`}
                >
                  <Icon className="w-8 h-8 text-rose-400" />
                  <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center bg-gradient-to-br from-rose-500 to-violet-600 text-white">
                    {num}
                  </span>
                </div>
                <div>
                  <p className="font-bold mb-1">{title}</p>
                  <p className={`text-sm leading-relaxed ${sub}`}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`h-[2px] w-full ${sep}`} />

      {/* ── CTA FINAL ─────────────────────────────────────── */}
      <div className="container mx-auto px-4 py-20 max-w-5xl">
        <div
          className={`relative overflow-hidden rounded-3xl border p-12 md:p-16 shadow-xl text-center ${
            isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"
          }`}
        >
          {/* halos */}
          <div className="pointer-events-none absolute inset-0">
            <div
              className={`absolute -left-20 -top-20 h-64 w-64 rounded-full blur-[100px] ${isDark ? "bg-rose-500/20" : "bg-rose-200/40"}`}
            />
            <div
              className={`absolute -right-20 -bottom-20 h-64 w-64 rounded-full blur-[100px] ${isDark ? "bg-violet-600/20" : "bg-violet-200/40"}`}
            />
          </div>

          <div className="relative space-y-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-rose-400">
              Prêt à démarrer ?
            </p>
            <h2 className="text-3xl lg:text-4xl font-extrabold">
              Un projet en tête ?{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
                }}
              >
                Parlons-en.
              </span>
            </h2>
            <p className={`max-w-lg mx-auto leading-relaxed ${sub}`}>
              Devis gratuit, réponse sous 24h, aucun engagement. Votre projet
              mérite une attention personnalisée.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-lg text-white transition-all hover:-translate-y-0.5"
              style={{
                background:
                  "linear-gradient(135deg, #f43f5e, #a855f7, #6366f1)",
              }}
            >
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
